import {
  EOtpAuthReasonCode,
  PORTAL_BOOT_TIMEOUT_MILLISECONDS,
  markOtpUrlAuthActive,
  readOtpCredentialsFromLocation,
  stripCredentialQueryParams,
} from '@/lib/otpAuth';
import type { IServerOtpLogin } from '@/lib/serverOtpLogin';
import { processBootData } from '@/lib/sharedBootUtils';
import type { PortalBootResponse } from '@/lib/types';

export interface ICapturedOtpLanding {
  proofKey: string;
  token: string;
}

interface IPortalBootState {
  stores?: string[];
  isFallbackBoot?: boolean;
}

export const captureOtpLandingFromWindow = (): ICapturedOtpLanding | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const credentials = readOtpCredentialsFromLocation(window.location.href);
  if (!credentials) {
    return undefined;
  }

  markOtpUrlAuthActive();
  window.history.replaceState({}, '', stripCredentialQueryParams(window.location.href));
  return credentials;
};

export const isPortalBootReady = (initialBoot: IPortalBootState | null | undefined): boolean =>
  initialBoot?.isFallbackBoot !== true && Boolean(initialBoot?.stores?.[0]);

export const fetchPortalBoot = async (bootRoute: string): Promise<PortalBootResponse> => {
  const response = await fetch(bootRoute, {
    signal: AbortSignal.timeout(PORTAL_BOOT_TIMEOUT_MILLISECONDS),
  });
  const bootJson = await response.json();

  return processBootData(bootJson);
};

export { EOtpAuthReasonCode };

/**
 * The server's exchange, once it has streamed in. Whatever goes wrong on the
 * way — the stream cut, the value malformed — is a login of our own, as today.
 */
export const settleServerOtpLogin = async (
  serverOtpLogin: Promise<IServerOtpLogin | undefined> | undefined
): Promise<IServerOtpLogin | undefined> => {
  if (!serverOtpLogin) {
    return undefined;
  }

  try {
    const settled = await serverOtpLogin;

    return settled && typeof settled.sessionId === 'string' ? settled : undefined;
  } catch {
    return undefined;
  }
};
